import type { NoteBody } from '@backrs_ui/grow-ui/src/redux/coachingDashboard/coachingDashboard.types';

export interface LexicalNode {
  type?: string;
  text?: string;
  children?: LexicalNode[];
  [key: string]: unknown;
}

export const walkLexical = (body: NoteBody | null, visit: (node: LexicalNode) => void): void => {
  const recurse = (node: LexicalNode | undefined): void => {
    if (!node) return;

    visit(node);
    if (Array.isArray(node.children)) node.children.forEach(recurse);
  };

  recurse(body?.root as LexicalNode | undefined);
};

export type TransformBodyResult =
  | { ok: true; body: NoteBody }
  | { ok: false; reason: 'imageNotReady' };

/** Strips the transient signed-URL src from image nodes; persisted nodes carry media_id instead. */
export const transformBodyForSave = (
  body: NoteBody,
  srcToMediaId: Map<string, number>,
): TransformBodyResult => {
  let imageNotReady = false;

  const rewrite = (node: LexicalNode): LexicalNode => {
    if (node.type === 'image' && typeof node.src === 'string') {
      const mediaId = srcToMediaId.get(node.src);

      if (!mediaId) {
        imageNotReady = true;

        return node;
      }

      return {
        type: 'image',
        version: 1,
        media_id: mediaId,
        alt: typeof node.alt === 'string' ? node.alt : '',
      };
    }

    if (Array.isArray(node.children)) {
      return { ...node, children: node.children.map(rewrite) };
    }

    return node;
  };

  const root = rewrite((body.root ?? {}) as LexicalNode);

  if (imageNotReady) return { ok: false, reason: 'imageNotReady' };

  return { ok: true, body: { ...body, root } };
};

export const countInlineImages = (body: NoteBody | null): number => {
  let count = 0;

  walkLexical(body, (node) => {
    if (node.type === 'image') count += 1;
  });

  return count;
};
